import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {useEffect, useMemo, useState, useCallback} from 'react';
import {GridColDef, GridRowSelectionModel, GridRenderCellParams} from '@mui/x-data-grid';
import {LoadingButton} from '@mui/lab';
import {OrgUser} from 'src/apis/autoGenerate/types';
import {
  orgUserServiceDelete,
  orgUserServiceFind,
  orgUserServiceBatchUpdateCallMethod,
} from 'src/apis/autoGenerate/services';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import message from 'src/components/Message';
import SearchInput from 'src/components/SearchInput';
import {formatDate, formatPhoneForDisplay} from 'src/utils/common';
import AddUserModal from 'src/components/Modals/AddUser';
import {useOrgId, useLicenseQuota} from 'src/hooks';
import {PageHeader, EntioDataGrid} from 'src/components/ui';
import {ReactComponent as SIPCallIcon} from 'src/assets/SIP_Call.svg';
import {ReactComponent as MobileAppIcon} from 'src/assets/Mobile_App.svg';

export default function UserPage() {
  const {orgId} = useOrgId();
  const [AddUserModalOpen, setAddUserModalOpen] = useState(false);
  const [searchUsers, setSearchUsers] = useState<string>('');
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrgUser>();
  const [userActionIdAnchorEl, setUserActionIdAnchorEl] = useState<HTMLButtonElement | null>(null);
  const userActionOpen = Boolean(userActionIdAnchorEl);
  const userActionId = userActionOpen ? 'user-action-popover' : undefined;
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
  const [batchCallMethodUpdate, setBatchCallMethodUpdate] = useState(false);
  const [batchUpdateLoading, setBatchUpdateLoading] = useState(false);
  const [batchCallMethodValue, setBatchCallMethodValue] = useState<'MOBILE_APP' | 'SIP_PHONE'>(
    'MOBILE_APP'
  );

  // SIP license quota gates all call method UI on this page
  const {
    hasSipLicense,
    sipAvailableUnits,
    sipHasUnlimited,
    refetch: refetchLicenseQuota,
  } = useLicenseQuota();

  const getCallMethodErrorMessage = (error: any): string => {
    const serverMessage =
      error?.response?.data?.error || error?.response?.data?.message || error?.message;

    if (typeof serverMessage === 'string') {
      if (serverMessage.includes('Insufficient SIP license quota')) {
        const availableMatch = serverMessage.match(/Available:\s*(-?\d+)/i);
        const neededMatch = serverMessage.match(/Needed:\s*(-?\d+)/i);
        const available = availableMatch ? availableMatch[1] : null;
        const needed = neededMatch ? neededMatch[1] : null;
        const suffix =
          available || needed ? ` (Available ${available ?? '?'}, Needed ${needed ?? '?'})` : '';
        return `SIP license quota is insufficient. Please add SIP licenses or free up SIP users.${suffix}`;
      }
      return serverMessage;
    }

    return 'Failed to update call method';
  };

  const getList = useCallback(
    async (name?: string) => {
      if (!orgId) return;
      setLoading(true);
      orgUserServiceFind(name, orgId, 500, undefined)
        .then(res => {
          const _users = res.data.users;
          if (_users?.length) {
            setUsers(_users);
          } else {
            setUsers([]);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [orgId]
  );

  useEffect(() => {
    if (orgId) {
      getList();
    }
  }, [orgId, getList]);

  useEffect(() => {
    if (!AddUserModalOpen) {
      setSelectedUser(undefined);
    }
  }, [AddUserModalOpen]);

  const handleActionClose = () => {
    setUserActionIdAnchorEl(null);
  };

  const handleSearchUsersByInfo = (info = '') => {
    setSearchUsers(info);
    getList(info);
  };

  const handleUserActionClick = (event: React.MouseEvent<HTMLButtonElement>, user: OrgUser) => {
    setSelectedUser(user);
    setUserActionIdAnchorEl(event.currentTarget);
  };

  const handleEditUser = () => {
    setUserActionIdAnchorEl(null);
    setAddUserModalOpen(true);
  };

  const handleRemoveUser = () => {
    setLoading(true);
    orgUserServiceDelete(selectedUser?.id as string).then(() => {
      message.success('Remove Success');
      getList(searchUsers);
    });
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'User Name',
        width: 100,
        flex: 0.7,
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 140,
        flex: 1,
      },
      {
        field: 'phone',
        headerName: 'Phone',
        width: 130,
        flex: 0.8,
        renderCell: (params: any) => {
          const phone = params.value;
          return (
            <Typography variant="body2" sx={{fontSize: '0.875rem'}}>
              {formatPhoneForDisplay(phone)}
            </Typography>
          );
        },
      },
      {
        field: 'role',
        headerName: 'User Type',
        width: 90,
        flex: 0.6,
        renderCell: (val: any) => {
          return <div>{val.value ? val.value.name : '-'}</div>;
        },
      },
      ...(hasSipLicense
        ? [
            {
              field: 'callMethod',
              headerName: 'Call Method',
              width: 130,
              flex: 0.8,
              renderCell: (params: GridRenderCellParams<OrgUser>) => {
                const user = params.row as OrgUser;
                const callMethod = user.callMethod || 'MOBILE_APP';
                const label = callMethod === 'SIP_PHONE' ? 'SIP Phone' : 'Mobile App';
                const Icon = callMethod === 'SIP_PHONE' ? SIPCallIcon : MobileAppIcon;

                return (
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Icon style={{width: 16, height: 16}} />
                    <Typography variant="body2" sx={{fontSize: '0.8rem'}}>
                      {label}
                    </Typography>
                  </Box>
                );
              },
            },
          ]
        : []),
      {
        field: 'createdAt',
        headerName: 'Create Date',
        width: 110,
        flex: 0.8,
        renderCell: (val: any) => {
          return <div>{formatDate(val.row.createdAt)}</div>;
        },
      },
      {
        field: 'id',
        headerName: '',
        width: 40,
        renderCell: val => {
          return (
            <IconButton
              onClick={e => {
                handleUserActionClick(e, val.row);
              }}
            >
              <MoreHorizIcon />
            </IconButton>
          );
        },
      },
    ],
    [hasSipLicense]
  );

  return (
    <Box>
      <PageHeader
        title="Users"
        actions={
          <>
            {hasSipLicense && (
              <Button
                variant="outlined"
                onClick={() => {
                  setBatchCallMethodUpdate(!batchCallMethodUpdate);
                }}
              >
                Batch Update Call Method
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => {
                setSelectedUser(undefined);
                setAddUserModalOpen(true);
              }}
              startIcon={<AddIcon />}
            >
              Add User
            </Button>
          </>
        }
      />
      <Box sx={{mb: 2}}>
        <SearchInput placeholder="Search users" onSearch={handleSearchUsersByInfo} />
      </Box>
      {batchCallMethodUpdate && (
        <Box
          sx={{
            bgcolor: 'rgba(233, 240, 255, 0.6)',
            p: '8px 16px',
            mb: 2,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            position: 'relative',
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
            <Typography variant="body2">{selectedRowIds.length} user(s) selected</Typography>
            {batchCallMethodValue === 'SIP_PHONE' && (
              <Typography variant="body2" sx={{color: 'text.secondary'}}>
                {sipHasUnlimited
                  ? 'Unlimited SIP licenses'
                  : `${sipAvailableUnits} SIP license(s) available`}
              </Typography>
            )}
            <FormControl size="small" sx={{minWidth: 150}}>
              <Select
                value={batchCallMethodValue}
                onChange={e =>
                  setBatchCallMethodValue(e.target.value as 'MOBILE_APP' | 'SIP_PHONE')
                }
                sx={{height: '32px'}}
              >
                <MenuItem value="MOBILE_APP">
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <MobileAppIcon style={{width: 16, height: 16}} />
                    <span>Mobile App</span>
                  </Box>
                </MenuItem>
                <MenuItem value="SIP_PHONE">
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <SIPCallIcon style={{width: 16, height: 16}} />
                    <span>SIP Phone</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <LoadingButton
              loading={batchUpdateLoading}
              loadingPosition="start"
              variant="contained"
              disabled={selectedRowIds.length < 1}
              onClick={async () => {
                if (selectedRowIds.length === 0) return;

                const orgUserIds = users
                  .filter(user => selectedRowIds.includes(user.id || ''))
                  .map(user => user.orgUserId)
                  .filter((id): id is string => Boolean(id));

                if (orgUserIds.length === 0) {
                  message.error('No valid org user IDs found for selected users');
                  setBatchUpdateLoading(false);
                  return;
                }

                // Only users not already on SIP consume a new seat
                if (batchCallMethodValue === 'SIP_PHONE' && !sipHasUnlimited) {
                  const seatsNeeded = users.filter(
                    user =>
                      selectedRowIds.includes(user.id || '') && user.callMethod !== 'SIP_PHONE'
                  ).length;

                  if (seatsNeeded > sipAvailableUnits) {
                    message.error(
                      `Not enough SIP licenses. This change needs ${seatsNeeded} more SIP seat(s) but only ${sipAvailableUnits} are available.`
                    );
                    return;
                  }
                }

                setBatchUpdateLoading(true);
                try {
                  const response = await orgUserServiceBatchUpdateCallMethod({
                    orgUserIds: orgUserIds,
                    callMethod: batchCallMethodValue,
                  });
                  if (response.data.success) {
                    message.success(
                      `Successfully updated ${response.data.updated} user(s). ${
                        response.data.failed > 0 ? `${response.data.failed} failed.` : ''
                      }`
                    );
                    if (response.data.errors && response.data.errors.length > 0) {
                      console.error('Update errors:', response.data.errors);
                    }
                    getList(searchUsers);
                    refetchLicenseQuota();
                    setSelectedRowIds([]);
                    setBatchCallMethodUpdate(false);
                    setBatchCallMethodValue('MOBILE_APP');
                  } else {
                    message.error('Failed to update call method');
                  }
                } catch (error) {
                  message.error(getCallMethodErrorMessage(error));
                } finally {
                  setBatchUpdateLoading(false);
                }
              }}
              size="small"
              sx={{borderRadius: 16}}
            >
              Update
            </LoadingButton>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setBatchCallMethodUpdate(false);
              setSelectedRowIds([]);
              setBatchCallMethodValue('MOBILE_APP');
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Box sx={{overflowX: 'auto'}}>
        <EntioDataGrid
          loading={loading}
          rows={users}
          columns={columns}
          emptyMessage="No users"
          emptyDescription="Add users to manage your organization."
          checkboxSelection={batchCallMethodUpdate}
          rowSelectionModel={selectedRowIds}
          onRowSelectionModelChange={res => {
            setSelectedRowIds(res);
          }}
          sx={{minWidth: 500}}
        />
      </Box>
      {AddUserModalOpen && (
        <AddUserModal
          open={AddUserModalOpen}
          initData={selectedUser}
          handleCancel={() => {
            setAddUserModalOpen(false);
          }}
          handleConfirm={() => {
            setAddUserModalOpen(false);
            getList();
            refetchLicenseQuota();
          }}
        />
      )}
      <Popover
        id={userActionId}
        open={userActionOpen}
        anchorEl={userActionIdAnchorEl}
        onClose={handleActionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleEditUser}>
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setUserActionIdAnchorEl(null);
                handleRemoveUser();
              }}
            >
              <ListItemText primary="Remove" />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
    </Box>
  );
}
